import React, { useState, useMemo, useEffect } from 'react';
import { X, Minus, Plus, ShoppingBag, ArrowRight, Truck, TicketPercent, CheckCircle, Trash2 } from 'lucide-react';
import { CartItem } from '../types';
import { PLACEHOLDER_IMAGE } from '../constants';
import axios from 'axios';

interface CartModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  tenantId: string;
  onUpdateQuantity: (id: string, delta: number) => void;
  onCheckout: (shippingData?: any, couponData?: any) => void;
  isSubmitting: boolean;
  primaryColor?: string;
  deliveryRadius?: number;
  originZipCode?: string;
  deliveryFixedPrice?: number;
  deliveryFeeType?: 'FIXED' | 'VARIABLE';
  showToast?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

const maskCEP = (value: string) => {
  return value
    .replace(/\D/g, '') // Remove o que não é número
    .replace(/(\d{5})(\d)/, '$1-$2') // Coloca o hífen após o 5º número
    .slice(0, 9); // Limita ao formato 00000-000
};

const CartModal: React.FC<CartModalProps> = ({
  isOpen, onClose, items, tenantId, onUpdateQuantity, onCheckout, isSubmitting,
  primaryColor = '#059669',
  deliveryRadius, originZipCode, deliveryFixedPrice, deliveryFeeType,
  showToast
}) => {
  const [zipCode, setZipCode] = useState('');
  const [shippingOptions, setShippingOptions] = useState<any[]>([]);
  const [selectedShipping, setSelectedShipping] = useState<any>(null);
  const [calculating, setCalculating] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  const getLightColor = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, 0.1)`;
  };

  const [isDistanceValid, setIsDistanceValid] = useState<boolean | null>(null);
  const [distanceError, setDistanceError] = useState<string>('');

  const handleCheckDistance = async () => {
    const cleanZip = zipCode.replace(/\D/g, '');
    if (cleanZip.length < 8) {
      showToast ? showToast("CEP incompleto.", "error") : alert("CEP incompleto.");
      return;
    }

    setCalculating(true);
    try {
      const res = await axios.post('https://zapshop.app/api/shipping/validate-radius', {
        tenantId,
        destZipCode: cleanZip
      });

      const distanceNum = parseFloat(res.data.distance) || 0;

      if (res.data.valid) {
        setIsDistanceValid(true);
        setDistanceError('');
        if (deliveryFeeType === 'FIXED') {
          setSelectedShipping({
            service_id: 'MOTOBOY',
            name: 'Delivery Local',
            price: typeof deliveryFixedPrice === 'string' ? parseFloat(deliveryFixedPrice) : (deliveryFixedPrice || 0),
            days: 'Hoje'
          });
        }
      } else {
        setIsDistanceValid(false);
        // CORREÇÃO AQUI: Garante que o número apareça formatado
        setDistanceError(`Fora da área de entrega (Limite: ${deliveryRadius}km), escolha Correios.`);
        setSelectedShipping(null);
      }
    } catch (err) {
      setDistanceError("CEP não localizado.");
    } finally {
      setCalculating(false);
    }
  };

  const availableModes = useMemo(() => {
    if (!items || items.length === 0) return [];
    let commonModes = new Set(['ME_CORREIOS', 'MOTOBOY', 'PICKUP', 'DIGITAL']);
    items.forEach(item => {
      let modes = Array.isArray(item.shipping_modes) ? item.shipping_modes : [];
      if (modes.length === 0) {
        if (item.shipping_mode === 'DELIVERY') modes = ['ME_CORREIOS'];
        else if (item.shipping_mode === 'DIGITAL') modes = ['DIGITAL'];
        else if (item.shipping_mode === 'PICKUP') modes = ['PICKUP'];
        else modes = ['ME_CORREIOS'];
      }
      commonModes = new Set([...commonModes].filter(x => modes.includes(x)));
    });
    return Array.from(commonModes);
  }, [JSON.stringify((items || []).map(i => ({ id: i.id, m: i.shipping_modes })))]);

  useEffect(() => {
    if (isOpen && availableModes.length > 0) {
      if (!activeTab || !availableModes.includes(activeTab)) {
        setActiveTab(availableModes.includes('ME_CORREIOS') ? 'ME_CORREIOS' : availableModes[0]);
      }
    }
  }, [availableModes, isOpen]);

  // --- CORREÇÃO MATEMÁTICA: Troca vírgula por ponto ---
  const safeNumber = (val: any) => {
    if (!val) return 0;
    const strVal = String(val).replace(',', '.');
    return Number(strVal) || 0;
  };

  const subtotal = items.reduce((sum, item) => {
    const price = safeNumber(item.price);
    const promo = safeNumber(item.promotional_price);
    const finalPrice = (promo > 0 && promo < price) ? promo : price;
    return sum + (finalPrice * item.quantity);
  }, 0);

  const discountValue = appliedCoupon ? (appliedCoupon.discount_type === 'PERCENTAGE' ? (subtotal * (Number(appliedCoupon.discount_value) / 100)) : Number(appliedCoupon.discount_value)) : 0;
  const total = subtotal + (selectedShipping ? parseFloat(selectedShipping.price) : 0) - discountValue;

  if (!isOpen) return null;

  const handleApplyCoupon = async () => {
    if (!couponCode) return;
    setIsValidatingCoupon(true);
    try {
      const res = await axios.post('https://zapshop.app/api/checkout/validate-coupon', {
        tenant_id: tenantId, code: couponCode.toUpperCase(), cart_total: subtotal
      });
      if (res.data.valid) {
        setAppliedCoupon(res.data.coupon);
        showToast ? showToast("Cupom aplicado!", "success") : alert("Cupom aplicado!");
      }
    } catch (err: any) {
      const errMsg = err.response?.data?.message || "Cupom inválido.";
      showToast ? showToast(errMsg, "error") : alert(errMsg);
      setAppliedCoupon(null);
    } finally { setIsValidatingCoupon(false); }
  };

  const handleCalculateShipping = async () => {
    if (zipCode.length < 8) {
      showToast ? showToast("CEP inválido.", "error") : alert("CEP inválido.");
      return;
    }
    setCalculating(true);
    try {
      const response = await axios.post('https://zapshop.app/api/shipping/calculate', { tenantId, destZipCode: zipCode, products: items });
      setShippingOptions(response.data);
    } catch (error: any) { alert(error.response?.data?.error || "Erro ao calcular frete."); } finally { setCalculating(false); }
  };

  const handleTabChange = (mode: string) => {
    if (mode === activeTab) return;
    setActiveTab(mode); setShippingOptions([]); setSelectedShipping(null);
    if (mode === 'MOTOBOY') setSelectedShipping({ service_id: 'MOTOBOY', name: 'Delivery Local', price: 0, days: 'Hoje/Amanhã' });
    else if (mode === 'PICKUP') setSelectedShipping({ service_id: 'PICKUP', name: 'Retirada', price: 0, days: 'Imediato' });
    else if (mode === 'DIGITAL') setSelectedShipping({ service_id: 'DIGITAL', name: 'Digital', price: 0, days: 'Imediato' });
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={onClose} />

      <div className="relative bg-white w-full max-w-md h-full shadow-2xl flex flex-col animate-slide-left">
        {/* Header */}
        <div className="px-6 py-5 border-b flex justify-between items-center bg-white">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <ShoppingBag style={{ color: primaryColor }} /> Sacola
          </h2>
          <button onClick={onClose}><X className="text-gray-400 hover:text-gray-600" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-gray-50/50">
          {items.map((item) => {
            const price = safeNumber(item.price);
            const promo = safeNumber(item.promotional_price);
            const finalPrice = (promo > 0 && promo < price) ? promo : price;

            return (
              <div key={item.id} className="flex gap-4 bg-white p-4 rounded-xl border border-gray-100 shadow-sm relative pr-10">
                {/* BOTÃO EXCLUIR */}
                <button
                  onClick={() => onUpdateQuantity(item.id, -item.quantity)}
                  className="absolute top-2 right-2 p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors z-10"
                  title="Remover produto"
                >
                  <Trash2 size={18} />
                </button>

                <img src={item.image_url ? `/api${item.image_url}` : PLACEHOLDER_IMAGE} className="w-16 h-16 rounded object-cover bg-gray-50" />

                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <h4 className="text-sm font-bold text-gray-800 line-clamp-2 pr-4">{item.name}</h4>

                    {item.selected_options && Object.entries(item.selected_options).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {Object.entries(item.selected_options).map(([key, val]) => (
                          <div key={key} className="flex items-center gap-1 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-md">
                            <span className="text-[9px] text-gray-400 uppercase font-bold">{key}:</span>
                            <span className="text-[9px] text-gray-700 font-extrabold uppercase">{val}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-1">
                      {finalPrice < price ? (
                        <div className="flex flex-col">
                          <span className="text-[10px] text-gray-400 line-through">R$ {price.toFixed(2).replace('.', ',')}</span>
                          <span className="text-sm font-bold" style={{ color: primaryColor }}>R$ {finalPrice.toFixed(2).replace('.', ',')}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500 font-medium">R$ {price.toFixed(2).replace('.', ',')}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => onUpdateQuantity(item.id, -1)} className="p-1 border rounded hover:bg-gray-50"><Minus size={12} /></button>
                    <span className="text-sm font-bold w-6 text-center">{item.quantity}</span>
                    <button onClick={() => onUpdateQuantity(item.id, 1)} className="p-1 border rounded hover:bg-gray-50"><Plus size={12} /></button>
                  </div>
                </div>

                <div className="text-right flex flex-col justify-end">
                  <span className="font-bold text-sm text-gray-900">R$ {(finalPrice * item.quantity).toFixed(2).replace('.', ',')}</span>
                </div>
              </div>
            );
          })}

          {/* Cupom */}
          <div className="bg-white p-4 rounded-xl border border-dashed border-gray-300 mt-4">
            <div className="flex gap-2">
              <input placeholder="CUPOM" value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())} className="flex-1 border p-2 rounded text-sm uppercase outline-none focus:ring-1" style={{ borderColor: isValidatingCoupon ? primaryColor : '#e5e7eb' }} />
              <button onClick={handleApplyCoupon} disabled={isValidatingCoupon} className="text-white px-4 rounded text-xs font-bold" style={{ backgroundColor: '#1f2937' }}>OK</button>
            </div>
            {appliedCoupon && <p className="text-xs mt-2 font-bold flex items-center gap-1" style={{ color: primaryColor }}><CheckCircle size={12} /> Desconto aplicado!</p>}
          </div>

          {/* Frete */}
          {items.length > 0 && availableModes.length > 0 && (
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mt-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-2">
                <Truck size={14} /> Escolha a forma de entrega:
              </h3>
              <div className="flex flex-col gap-2 mb-4">
                {availableModes.map(m => {
                  const isActive = activeTab === m;
                  return (
                    <button
                      key={m}
                      onClick={() => handleTabChange(m)}
                      className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-all text-sm font-bold ${isActive ? 'ring-1' : 'hover:bg-gray-50 border-gray-200 text-gray-600'}`}
                      style={isActive ? { backgroundColor: getLightColor(primaryColor), color: primaryColor, borderColor: primaryColor, ringColor: primaryColor } : {}}
                    >
                      <span>
                        {m === 'ME_CORREIOS' ? 'Correios / Transportadora' :
                          m === 'MOTOBOY' ? 'Delivery Local (Motoboy)' :
                            m === 'PICKUP' ? 'Retirada na Loja' : 'Produto Digital'}
                      </span>
                      {isActive && <CheckCircle size={18} style={{ color: primaryColor }} />}
                    </button>
                  );
                })}
              </div>
              {activeTab === 'ME_CORREIOS' && (
                <div className="animate-fade-in border-t pt-4 border-gray-100">
                  <div className="flex gap-2 mb-2">
                    <input
                      placeholder="00000-000"
                      value={zipCode}
                      onChange={e => { setZipCode(maskCEP(e.target.value)); setIsDistanceValid(null); }}
                      maxLength={9}
                      className="border p-2 rounded w-full text-sm outline-none"
                    />
                    <button onClick={handleCalculateShipping} disabled={calculating} className="text-white px-4 py-2 rounded text-sm font-bold" style={{ backgroundColor: primaryColor }}>{calculating ? '...' : 'Calcular'}</button>
                  </div>
                  {shippingOptions.map(opt => (
                    <div key={opt.service_id} onClick={() => setSelectedShipping(opt)} className="p-3 border rounded-lg mt-2 cursor-pointer flex justify-between items-center hover:bg-gray-50"
                      style={selectedShipping?.service_id === opt.service_id ? { borderColor: primaryColor, backgroundColor: getLightColor(primaryColor) } : {}}>
                      <div><span className="text-sm font-bold text-gray-800">{opt.name}</span><span className="block text-xs text-gray-500">{opt.days} dias úteis</span></div>
                      <span className="font-bold text-gray-900">R$ {(Number(opt.price) || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              {activeTab === 'MOTOBOY' && (
                <div className="animate-fade-in border-t pt-4 border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 mb-2 uppercase">Validar endereço de entrega:</p>
                  <div className="flex gap-2 mb-2">
                    {/* Linha 270: Altere o input do bloco MOTOBOY */}
                    <input
                      placeholder="00000-000"
                      value={zipCode}
                      onChange={e => {
                        setZipCode(maskCEP(e.target.value));
                        setIsDistanceValid(null);
                      }}
                      maxLength={9}
                      className="border p-2 rounded w-full text-sm outline-none"
                    />
                    <button
                      onClick={handleCheckDistance}
                      disabled={calculating}
                      className="text-white px-4 py-2 rounded text-sm font-bold"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {calculating ? '...' : 'Validar'}
                    </button>
                  </div>
                  {isDistanceValid === false && <p className="text-red-500 text-[11px] font-bold">{distanceError}</p>}
                  {isDistanceValid === true && (
                    <p className="text-green-600 text-[11px] font-bold flex items-center gap-1">
                      <CheckCircle size={12} /> Endereço dentro do raio de atendimento!
                    </p>
                  )}
                </div>
              )}
              {activeTab === 'PICKUP' && <div className="text-center text-xs text-gray-500 bg-gray-50 p-3 rounded border">Endereço de retirada será enviado após o pedido.</div>}
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-white">
          <div className="space-y-1 mb-4 text-sm">
            <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>R$ {subtotal.toFixed(2)}</span></div>
            {selectedShipping && <div className="flex justify-between font-medium" style={{ color: primaryColor }}><span>Frete</span><span>+ R$ {(Number(selectedShipping.price) || 0).toFixed(2)}</span></div>}
            {discountValue > 0 && <div className="flex justify-between font-medium" style={{ color: primaryColor }}><span>Desconto</span><span>- R$ {discountValue.toFixed(2)}</span></div>}
            <div className="flex justify-between text-xl font-bold text-gray-900 pt-2 border-t mt-2"><span>Total</span><span>R$ {total.toFixed(2)}</span></div>
          </div>

          <button onClick={() => onCheckout(selectedShipping, appliedCoupon)}

            disabled={
              isSubmitting ||
              (activeTab === 'MOTOBOY' && isDistanceValid !== true) ||
              (activeTab === 'ME_CORREIOS' && !selectedShipping) ||
              (items.length === 0)
            }

            className="w-full text-white py-3.5 rounded-xl font-bold transition-all shadow-lg hover:brightness-110 disabled:opacity-50 disabled:shadow-none"
            style={{ backgroundColor: primaryColor, boxShadow: `0 10px 15px -3px ${getLightColor(primaryColor)}` }}>
            {isSubmitting ? 'Processando...' : 'Finalizar Pedido via WhatsApp'}
          </button>
        </div>
      </div>
    </div>
  );
};
export default CartModal;
